import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { ORGANIZATION_ADMIN_ROUTES } from './organization-admin.routes';
import { OrganizationFormFactory, type OrganizationFormModel } from './organization-form';
import { OrganizationAdminApiService } from '../services/organization-admin-api.service';
import type { OrganizationDto } from '../../shared';
import { TableStateService, injectMutationActions } from '@mb/components';
import { clientLogger } from '@mb/common/client';

export function createSaveOrganizationAction() {
  const api = inject(OrganizationAdminApiService);
  const mutationActions = injectMutationActions();
  const tableState = inject(TableStateService<OrganizationDto>);
  const router = inject(Router);
  const routes = inject(ORGANIZATION_ADMIN_ROUTES);

  const onError = (error: unknown) => {
    clientLogger.error('Error submitting organization form', {
      error: error instanceof Error ? error : new Error(String(error)),
    });
  };

  async function onSaveSuccess(row: OrganizationDto, model: OrganizationFormModel): Promise<void> {
    tableState.upsertRow(row);

    const selected = tableState.selected();
    if (selected && String(selected.id) === String(row.id)) {
      tableState.patchSelectedDetails({
        ...(selected as OrganizationDto),
        ...row,
        name: model.name,
      });
    }

    tableState.refreshList();
    await router.navigate(routes.detail(row.id));
  }

  return async (model: OrganizationFormModel): Promise<void> => {
    if (model.id) {
      await mutationActions.updateWithToast({
        onError,
        mutate: (input) => api.updateOrganization(input),
        input: OrganizationFormFactory.toUpdatePayload(model),
        onSuccess: async (result) => {
          if (!result.data) {
            return;
          }
          await onSaveSuccess(result.data, model);
        },
      });
      return;
    }

    await mutationActions.addWithToast({
      onError,
      mutate: (input) => api.createOrganization(input),
      input: OrganizationFormFactory.toCreatePayload(model),
      onSuccess: async (result) => {
        if (!result.data) {
          return;
        }
        await onSaveSuccess(result.data, model);
      },
    });
  };
}

export function createDeleteOrganizationAction() {
  const mutationActions = injectMutationActions();
  const api = inject(OrganizationAdminApiService);
  const tableState = inject(TableStateService<OrganizationDto>);
  const router = inject(Router);
  const routes = inject(ORGANIZATION_ADMIN_ROUTES);

  return async (id: string): Promise<void> => {
    await mutationActions.confirmAndDelete({
      mutate: (organizationId) => api.deleteOrganization(organizationId),
      input: id,
      onSuccess: async () => {
        tableState.removeRow(id);
        tableState.refreshList();
        await tableState.selectById(null);
        await router.navigate([...routes.list]);
      },
    });
  };
}
